import { PlayerPublic, PotInfo, Card as CardType } from '../shared/types';
import { PlayerSeat } from './PlayerSeat';
import { PlayingCard } from './Card';
import { PotDisplay } from './Pot';

// Позиции мест вокруг овального стола в процентах (для 6-max и heads-up).
// Место 0 всегда "снизу по центру" — туда сажаем текущего зрителя для привычного UX.
const SEAT_POSITIONS_6: string[] = [
  'bottom-1 left-1/2 -translate-x-1/2', // 0 - низ (я)
  'bottom-10 left-2 ', // 1 - низ-лево
  'top-20 left-2', // 2 - верх-лево
  'top-1 left-1/2 -translate-x-1/2', // 3 - верх (по центру)
  'top-20 right-2', // 4 - верх-право
  'bottom-10 right-2', // 5 - низ-право
];

const SEAT_POSITIONS_HU: string[] = [
  'bottom-1 left-1/2 -translate-x-1/2', // 0 - я
  'top-1 left-1/2 -translate-x-1/2', // 1 - оппонент
];

interface TableFeltProps {
  players: (PlayerPublic | null)[]; // длина = maxSeats, распределено по местам, начиная с viewer'а на позиции 0
  communityCards: CardType[];
  pots: PotInfo[];
  actingPlayerId: string | null;
  actionDeadline: number | null;
  viewerPlayerId: string;
  winners: { playerId: string; amount: number }[] | null;
  winningCardKeys: Set<string>; // "rank+suit" карт, вошедших в выигрышную комбинацию — подсвечиваем на столе
  onSitSeat: (seatIndex: number) => void;
}

function cardKey(c: CardType) {
  return `${c.rank}${c.suit}`;
}

export function TableFelt({
  players,
  communityCards,
  pots,
  actingPlayerId,
  actionDeadline,
  viewerPlayerId,
  winners,
  winningCardKeys,
  onSitSeat,
}: TableFeltProps) {
  const positions = players.length === 2 ? SEAT_POSITIONS_HU : SEAT_POSITIONS_6;

  return (
    <div className="relative w-full aspect-[10/13] max-w-sm mx-auto">
      {/* Деревянный рейл по внешнему краю стола — премиальная кромка. Рисуется первым (снизу по стеку). */}
      <div
        className="absolute inset-3 rounded-[48%] bg-cover bg-center opacity-90"
        style={{ backgroundImage: `url(/textures/wood.jpg)` }}
      />
      <div
        className="absolute inset-6 rounded-[45%] border-[6px] border-ink-800 shadow-felt bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(10,36,23,0.55), rgba(4,20,13,0.85)), url(/textures/felt.jpg)`,
        }}
      />
      <div className="absolute inset-6 rounded-[45%] border-2 border-gold-500/20" />

      {/* Общие карты + банк — центр стола */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <div className="flex gap-1.5">
          {Array.from({ length: 5 }).map((_, i) => {
            const card = communityCards[i] ?? null;
            return (
              <PlayingCard
                key={i}
                card={card}
                size="md"
                delay={i * 0.08}
                highlight={card ? winningCardKeys.has(cardKey(card)) : false}
              />
            );
          })}
        </div>
        <PotDisplay pots={pots} pulseOnGrow />
      </div>

      {players.map((player, i) => (
        <PlayerSeat
          key={i}
          player={player}
          isViewer={player?.id === viewerPlayerId}
          isActing={player?.id === actingPlayerId}
          actionDeadline={player?.id === actingPlayerId ? actionDeadline : null}
          winningsAmount={winners?.find((w) => w.playerId === player?.id)?.amount}
          onSitHere={() => onSitSeat(i)}
          seatPositionClass={positions[i]}
        />
      ))}
    </div>
  );
}
